import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAnimatedDrawer } from "./useAnimatedDrawer";
import { popDrawerStack, pushDrawerStack } from "./routeDrawerStack";
/**
 * Overlay routes belong to React Router; closing one cannot rewrite another route.
 * Con `stack`: aprire una scheda mentre il drawer e' gia' aperto (es. la persona
 * dalla scheda azienda) mette la precedente in `${key}Back`, e chiudere torna a
 * quella invece di chiudere tutto.
 */
export function useRouteDrawer(key: string, options: { stack?: boolean } = {}) {
  const [params, setParams] = useSearchParams();
  const drawer = useAnimatedDrawer(params.get(key));
  const backKey = `${key}Back`;
  const stack = !!options.stack;
  const open = useCallback(
    (id: string) =>
      setParams((previous) => {
        const next = new URLSearchParams(previous);
        if (stack) {
          const back = pushDrawerStack(previous.get(backKey), previous.get(key), id);
          if (back) next.set(backKey, back);
          else next.delete(backKey);
        }
        next.set(key, id);
        return next;
      }),
    [key, backKey, stack, setParams],
  );
  const close = useCallback(
    () =>
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          const popped = stack ? popDrawerStack(previous.get(backKey)) : null;
          if (popped) {
            next.set(key, popped.current);
            if (popped.back) next.set(backKey, popped.back);
            else next.delete(backKey);
          } else {
            next.delete(key);
            next.delete(backKey);
          }
          return next;
        },
        { replace: true },
      ),
    [key, backKey, stack, setParams],
  );
  /** true se chiudere riporta a una scheda precedente invece di chiudere tutto */
  const hasBack = stack && !!params.get(backKey);
  return { ...drawer, open, close, hasBack };
}
